import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { USER_ROLE } from "../../../constants/app";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import { User, UserRole, userActions } from "../../../ducks/user";
import { useNavigation } from "../../../hooks/useNavigation";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { DetailItem } from "../../templates/DetailTemplate";

export const useUserDetail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const { userId } = useParams<{ userId: string }>();

  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    if (user || !userId) return;

    dispatch(appActions.showLoading());

    dispatch(
      userActions.getUserDetails.started({
        accountId: userId,
        onComplete: () => dispatch(appActions.hideLoading()),
        onSuccess: (data) => {
          const userData = data.data;
          const user: User = {
            id: userData.id,
            name: `${userData.last_name} ${userData.first_name}`,
            firstName: userData.first_name,
            lastName: userData.last_name,
            email: userData.email,
            phoneNumber: userData.phone,
            clientName: userData.customer_name,
            groupName: userData.group_name,
            role:
              userData.role === USER_ROLE.SYSTEM_ADMINISTRATOR
                ? UserRole.Admin
                : userData.role === USER_ROLE.USER
                  ? UserRole.User
                  : UserRole.GroupAdmin,
            systemPermissions: userData.system_permission,
            validPeriod: {
              from: new Date(userData.valid_from),
              to: new Date(userData.valid_to),
            },
            lastLogin: userData.last_login
              ? new Date(userData.last_login)
              : null,
            createdAt: new Date(userData.created_at),
            createdBy: userData.created_by,
          };

          setUser(user);
        },
        onError: () => {
          navigateToPage(PageRoutes.USERS);
        },
      }),
    );
  }, []);

  const userDetail: DetailItem[] = useMemo(() => {
    if (!user) return [];

    return [
      {
        name: "氏名",
        value: user.name,
      },
      {
        name: "メールアドレス",
        value: user.email ?? "",
      },
      {
        name: "電話番号",
        value: user.phoneNumber ?? "",
      },
      {
        name: "所属組織",
        value: user.clientName,
      },
      {
        name: "所属グループ",
        value: user.groupName,
      },
      {
        name: "権限",
        value: user.role,
      },
      {
        name: "有効期間",
        value: `${formatDateTime(user.validPeriod.from, false, "YYYY/MM/DD HH:mm")} 〜 ${formatDateTime(user.validPeriod.to, false, "YYYY/MM/DD HH:mm")}`,
      },
      {
        name: "システム許可",
        value: user.systemPermissions.join("／"),
      },
      {
        name: "最終ログイン",
        value: user.lastLogin
          ? formatDateTime(user.lastLogin, false, "YYYY/MM/DD HH:mm")
          : "未ログイン",
      },
      {
        name: "登録日",
        value: user.createdAt
          ? formatDateTime(user.createdAt, false, "YYYY/MM/DD HH:mm")
          : "",
      },
      {
        name: "登録者",
        value: user.createdBy ?? "",
      },
    ];
  }, [user]);

  const handleCancel = useCallback(() => {
    navigateToPage(PageRoutes.USERS);
  }, []);

  const handleEdit = useCallback(() => {
    if (!userId) return;

    navigateToPage(PageRoutes.USER_EDIT.replace(":userId", userId));
  }, [userId]);

  return { userDetail, handleCancel, handleEdit };
};
